import React, { Component } from 'react';
import { observable, action, makeObservable, runInAction, reaction, type IReactionDisposer } from 'mobx';
import { observer } from 'mobx-react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

import type { Message, ChatResponseItem, ChatResponseItemSettings, ToolApprovalDecision, Model } from '@root/models.ts';
import apiService from '@services/apiService.ts';
import messageService from '@services/messageService.ts';
import modelService from '@services/modelService.ts';
import { userService } from '@services/userService.ts';
import { chatExecutionService } from '@services/chatExecutionService.ts';
import skillService, { type SkillDto } from '@services/skillService.ts';
import { toastService } from '@services/toastService.ts';
import statsVisibilityService from '@services/statsVisibilityService.ts';

import { Modals, modalService, ModalButtonType } from '@components/Modal.tsx';
import ModelSelector from '@components/ModelSelector.tsx';
import { ChatMessage } from '@components/ChatMessage.tsx';
import { TaskPlanPanel } from '@components/TaskPlanPanel.tsx';
import { BlockLoader } from '@components/BlockLoader.tsx';
import ContextMenu, { ContextMenuItem, ContextMenuSeparator } from '@components/ContextMenu/index.ts';
import PromptArea from './PromptArea.tsx';
import Sidebar from './Sidebar.tsx';
import ChatSuggestions, { type ImageSuggestionItem } from './ChatSuggestions.tsx';
import type { SuggestionsAdminPageProps } from '@pages/SuggestionsAdminPage';
import FollowUpSuggestions from './FollowUpSuggestions.tsx';
const ShareDialog = React.lazy(() => import('./ShareDialog.tsx'));
const ImageGallery = React.lazy(() => import('./ImageGallery.tsx'));
const FilesBrowser = React.lazy(() => import('./FilesBrowser.tsx'));

const SettingsDialog = React.lazy(() => import('@components/SettingsDialog/SettingsDialog.tsx'));
const ModelUpdateNotification = React.lazy(() => import('@components/ModelUpdateNotification.tsx'));
const ChatConfigDialog = React.lazy(() => import('@components/ChatSettings/ChatConfigDialog.tsx'));
import { type ChatConfigDialogProps } from '@components/ChatSettings/ChatConfigDialog.tsx';
const RenameChatDialog = React.lazy(() => import('./RenameChatDialog.tsx'));
import type { RenameChatDialogProps } from './RenameChatDialog.tsx';
const ScheduleDialog = React.lazy(() => import('./ScheduleDialog.tsx'));
const ServerStatusBadge = React.lazy(() => import('@components/ServerStatusBadge.tsx'));
import type { ScheduleDialogProps, ScheduleDialogResult } from './ScheduleDialog.tsx';

import { imageService } from '@services/imageService.ts';
import type { ImageGenModel } from './ImageModelPicker.tsx';
import type { VideoGenModel } from './VideoModelPicker.tsx';
import { parseLoraStyleName } from './ImageStylePicker.tsx';
import type { ImageStyleSelection } from './ImageStylePicker.tsx';
import { ImgOpts } from '@components/Images/ImgOpts.tsx';
import { ImgSearch } from '@components/Images/ImgSearch.tsx';
import { ImgNewChat } from '@components/Images/ImgNewChat.tsx';
import { ImgStar } from '@components/Images/ImgStar.tsx';
import { ImgMenuMoreHorizontal } from '@components/Images/ImgThreeDots.tsx';
import { ImgMenu } from '@components/Images/ImgMenu.tsx';
import { ImgFolder } from '@components/Images/ImgFolder.tsx';

type UpdateMessageResult = {
  chatId: string;
  messages: { oldId: string; id: string; attachmentIds: string[] }[];
};

type ChatProps = {
  initialChatId?: string;
  initialFilesPath?: string | null;
  navigate?: (path: string) => void;
  children?: React.ReactNode;
};

@observer
class Chat extends Component<ChatProps> {
  @observable private _isSidebarOpen: boolean = window.innerWidth >= 768;
  @observable private _showImagesGallery: boolean = false;
  @observable private _filesBrowserPath: string | null = null;
  @observable private _isMessageEditing: boolean = false;
  @observable private _isImageGenMode: boolean = localStorage.getItem('isImageGenMode') === 'true';
  @observable private _currentModel: Model | null = null;
  @observable.ref private _messagesContainerRef: React.RefObject<HTMLDivElement | null> = React.createRef();
  @observable.ref private _promptAreaRef: React.RefObject<PromptArea | null> = React.createRef();
  @observable.ref private _chatOptionsButton: React.RefObject<HTMLButtonElement | null> = React.createRef();
  @observable.ref private _sidebarRef: React.RefObject<Sidebar | null> = React.createRef();
  @observable private _modelUpdateAvailable: { modelName: string; currentDigest?: string; latestDigest?: string } | null = null;
  @observable private _isTounched: boolean = false;
  private _isProcessingShare: boolean = false;

  private _modelSelectorRef = React.createRef<ModelSelector>();
  private _disableAutoScroll: boolean = false;
  private _activeChatReactionDisposer: IReactionDisposer | null = null;
  private _archiveReactionDisposer: IReactionDisposer | null = null;

  constructor(props: ChatProps) {
    super(props);
    this._filesBrowserPath = props.initialFilesPath ?? null;
    makeObservable(this);
  }

  async componentDidMount() {
    chatExecutionService.registerCallbacks({
      autoScroll: (opts) => this._autoScrollMessages(opts),
      savePrompt: () => this._promptAreaRef.current?.save(),
      refreshModels: () => modelService.loadActiveModels(),
    });

    document.addEventListener('visibilitychange', this._onDocumentVisibilitychange);
    document.addEventListener('touchstart', this._onTouchStart);
    document.addEventListener('touchend', this._onTouchEnd);

    // Remove the initial page loader immediately — loading progress is shown in the sidebar
    document.getElementsByClassName('ui-loader-container')[0]?.remove();

    this._activeChatReactionDisposer = reaction(
      () => chatExecutionService.activeChatId,
      (chatId) => {
        // Don't navigate while a shared payload is being staged — the share flow
        // owns navigation to '/' and a remount would drop the composer ref.
        if (this._isProcessingShare) return;
        runInAction(() => { this._filesBrowserPath = null; });
        const newPath = chatId ? `/c/${chatId}` : '/';
        if (window.location.pathname !== newPath) {
          this.props.navigate?.(newPath);
        }
      }
    );

    const loadChatId = sessionStorage.getItem('loadChatId') || this.props.initialChatId || new URLSearchParams(window.location.search).get('chat');
    if (loadChatId) sessionStorage.removeItem('loadChatId');

    // PWA web share target: shared content arrives via /share-inbox/<token> (or a
    // stashed token after the logged-out login flow). Stage it in a fresh chat.
    const stashedShareToken = sessionStorage.getItem('shareToken');
    if (stashedShareToken) sessionStorage.removeItem('shareToken');
    const shareToken = window.location.pathname.match(/^\/share-inbox\/([0-9a-f]+)/)?.[1] || stashedShareToken;
    if (shareToken) {
      if (sessionStorage.getItem('shareToken')) sessionStorage.removeItem('shareToken');
      void this._processShareInbox(shareToken);
    }

    // Archived chats are only fetched when the "Archive" setting is on. The reaction
    // covers both the initial state (fireImmediately) and live setting changes
    // (userService.loadUserSettings() updates the observable when settings are saved).
    this._archiveReactionDisposer = reaction(
      () => userService.showArchive,
      (showArchive) => {
        if (showArchive) {
          void chatExecutionService.loadArchivedChats();
        } else {
          runInAction(() => { chatExecutionService.archivedChats = []; });
        }
      },
      { fireImmediately: true }
    );

    // Load chat list and (if chatId is already known) chat messages in parallel for faster startup.
    // If the sidebar starts collapsed (mobile default) there's nothing to display it in —
    // skip the fetch; it's loaded lazily on the first expand (_onSidebarToggle / _onToggleSidebarClick).
    const loadChatList = this._isSidebarOpen;
    await Promise.all([
      loadChatList ? chatExecutionService.loadChats(false) : Promise.resolve(),
      loadChatId
        ? chatExecutionService.loadChatMessages(loadChatId, {
            modelSelectorRef: this._modelSelectorRef,
            sidebarRef: this._sidebarRef,
            onCheckModelUpdate: (model) => this._checkModelUpdate(model),
          })
        : Promise.resolve(),
    ]);

    // After both complete, ensure model is set (messages may have loaded before the chat list was ready)
    if (loadChatId) {
      let chat = chatExecutionService.chats.find(c => c.id === loadChatId);
      if (!chat && !loadChatList) {
        // The chat list wasn't fetched (sidebar started collapsed) — fetch just this chat for its model
        chat = await apiService.get<ChatResponseItem>(`/api/chats/${loadChatId}`);
      }
      if (chat?.model) {
        this._modelSelectorRef.current?.trySetModel(chat.model);
        this._checkModelUpdate(chat.model);
      }
    }
  }


  private async _processShareInbox(token: string): Promise<void> {
    this._isProcessingShare = true;
    try {
      await this._doProcessShareInbox(token);
    } finally {
      this._isProcessingShare = false;
    }
  }

  private async _doProcessShareInbox(token: string): Promise<void> {
    const fetchPayload = async () => {
      const res = await fetch(`/share-inbox/${token}`);
      if (!res.ok) throw new Error(`Share inbox fetch failed (${res.status})`);
      return res.json() as Promise<{ text: string; files: { name: string; type: string; base64: string }[] }>;
    };
    let payload: { text: string; files: { name: string; type: string; base64: string }[] };
    try {
      payload = await fetchPayload();
    } catch (e) {
      // The SW may be starting up — retry once after it's ready
      if ('serviceWorker' in navigator) {
        try {
          await navigator.serviceWorker.ready;
          payload = await fetchPayload();
        } catch (e2) {
          toastService.error('Share failed', 'Could not read the shared content. Please share it again.');
          this.props.navigate?.('/');
          return;
        }
      } else {
        toastService.error('Share failed', 'Could not read the shared content. Please share it again.');
        this.props.navigate?.('/');
        return;
      }
    }

    chatExecutionService.startNewChat(null);

    const files: File[] = payload.files.map(f => {
      const binary = atob(f.base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      return new File([bytes], f.name, { type: f.type });
    });

    const promptArea = this._promptAreaRef.current;
    if (promptArea) {
      await promptArea.processShareFiles(files, payload.text || '');
    } else {
      toastService.error('Share failed', 'Composer is not ready. Please share it again.');
    }
    this.props.navigate?.('/');
  }

  componentWillUnmount(): void {
    document.removeEventListener('visibilitychange', this._onDocumentVisibilitychange);
    window.removeEventListener('online', this._onOnlineChange);
    document.removeEventListener('touchstart', this._onTouchStart);
    document.removeEventListener('touchend', this._onTouchEnd);
    if (this._autoScrollTimerId !== null) { clearTimeout(this._autoScrollTimerId); this._autoScrollTimerId = null; }
    this._activeChatReactionDisposer?.();
    this._activeChatReactionDisposer = null;
    this._archiveReactionDisposer?.();
    this._archiveReactionDisposer = null;
  }

  @action private _onTouchStart = () => { this._isTounched = true; };
  @action private _onTouchEnd = () => { this._isTounched = false; };

  @action
  private _onDocumentVisibilitychange = () => {
    if (document.visibilityState === 'visible') chatExecutionService.loadChats(false, true);
  };

  @action
  private _onOnlineChange = () => { chatExecutionService.loadChats(false, true); };

  private _disableAutoScrollTimeoutId: number | null = null;
  @action
  private _onMessagesContainerScroll = () => {
    const el = this._messagesContainerRef.current;
    if (!el) return;
    // Only pause auto-scroll when the user scrolls away from the bottom.
    // Programmatic follow-scrolls land at the bottom — and since scroll
    // events fire asynchronously, a flag set-and-reset around the scroll
    // assignment can never actually catch them (that was the old stuck bug).
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom <= 100) return;
    this._disableAutoScroll = true;
    if (this._disableAutoScrollTimeoutId) clearTimeout(this._disableAutoScrollTimeoutId);
    this._disableAutoScrollTimeoutId = window.setTimeout(() => { 
      this._disableAutoScroll = false; 
      this._disableAutoScrollTimeoutId = null;
    }, 1000);
  };

  @action
  _onModelChange = (model: Model | null) => {
    this._currentModel = model;
    chatExecutionService.setCurrentModel(model);
    if (model) this._checkModelUpdate(model.name);
  };

  @action
  private _onSidebarToggle = async (isVisible: boolean) => {
    this._isSidebarOpen = isVisible;
    if (isVisible) await chatExecutionService.loadChats(false, true);
  };

  render() {
    const svc = chatExecutionService;
    const chatId = svc.activeChatId;
    const session = svc.activeSession;
    const chat = chatId ? svc.chats.find(c => c.id === chatId) || null : null;
    const conversation = svc.getMessageChain(chatId, false, true); // always collapse tool calls
    // Steering ("upcoming") messages queued while the model is generating. They are
    // excluded from `conversation` (getMessageChain filters them out) and picked up by
    // the server loop at the end of each LLM round.
    const upcomingMessages = chatId ? svc.getUpcomingMessages(chatId) : [];
    // Include default system prompt + important info for accurate token estimation.
    // Never collapse tool-call chains here — the counter needs every message, including
    // intermediate tool calls and tool results that are hidden from the display view.
    const tokenConversation = svc.getMessageChain(chatId, true, false);

    const hasSystemPrompt = conversation.length > 0 && conversation[0].role === 'system';
    const hasModel = this._currentModel !== null;
    const lastAssistantMessage = conversation.slice().reverse().find(msg => msg.role === 'assistant');

    // Flat ordered list of all server-stored image URLs across the conversation for cross-message gallery navigation
    const chatImageFileList: string[] = conversation.flatMap(msg =>
      (msg.attachmentMetas ?? []).filter(m => m.isImage).map(m => `/image/${m.id}`)
    );
    const tokensInConversation = (lastAssistantMessage?.tokensIn || 0) + (lastAssistantMessage?.tokensOut || 0);

    return (
      <>
        <Sidebar
          ref={this._sidebarRef}
          chats={svc.chats}
          archivedChats={svc.archivedChats}
          activeChatId={chatId}
          generatingChatIds={svc.generatingChatIds}
          onNewChat={this._onNewChatClick}
          onDeleteChat={this._deleteChat}
          onShareChat={this._shareChat}
          onFavoriteChat={this._favoriteChat}
          onArchiveChat={this._archiveChat}
          onUnarchiveChat={this._unarchiveChat}
          onSelect={this._onSelectChat}
          onToggle={this._onSidebarToggle}
          onRenameChat={this._renameChatFromSidebar}
          onRegenerateChat={(id) => svc.generateChatName(id, this._currentModel?.server)}
          onScheduleChat={this._scheduleChat}
          onShowGallery={this._onShowGallery}
          showGallery={this._showImagesGallery}
        />

        <div className={'main-content' + (this._isSidebarOpen ? ' sidebar-open' : '')} style={{ position: 'relative' }}>
          {this.props.children ? (
            <div className="suggestions-embed">
              {React.isValidElement(this.props.children)
                ? React.cloneElement(this.props.children as React.ReactElement<SuggestionsAdminPageProps>, { onToggleSidebar: this._onToggleSidebarClick, isSidebarOpen: this._isSidebarOpen })
                : this.props.children}
            </div>
          ) : (
            <>
          <div className="header">
            <button className={`burger-menu${this._isSidebarOpen ? ' burger-menu--collapsed' : ''}`} onClick={this._onToggleSidebarClick}><ImgMenu size={20} /></button>
            <ModelSelector ref={this._modelSelectorRef} onModelChange={this._onModelChange} model={this._currentModel} />

            <div className="header-actions">
              <button className="header-btn" onClick={this._onShowOptionsModal} title="Options">
                <ImgOpts />
              </button>
              {svc.chats.length > 1 && (
                <button className={`header-btn header-btn-sidebar-fade${this._isSidebarOpen ? ' header-btn-sidebar-fade--hidden' : ''}`} onClick={this._onSearchChatsClick} title="Search">
                  <ImgSearch />
                </button>
              )}
              {chatId && (
                <button className={`header-btn header-btn-sidebar-fade${this._isSidebarOpen ? ' header-btn-sidebar-fade--hidden' : ''}`} onClick={this._onNewChatClick} title="Start New Chat">
                  <ImgNewChat />
                </button>
              )}
              <div className="header-actions-filler"></div>
              <button className="header-btn ssb-header-btn" aria-hidden="true" tabIndex={-1}>
                {window.user?.role === 'admin' && <ServerStatusBadge /> }
              </button>
              <button ref={this._chatOptionsButton} className="header-btn header-btn-right" title="Chat options">
                <ImgMenuMoreHorizontal />
              </button>
              <ContextMenu container={this._chatOptionsButton} showOnClick>
                {() => (
                  <>
                    <ContextMenuItem
                      title="Regenerate Chat Name"
                      disabled={!hasModel || session.loading.isPending}
                      onClick={() => chatId && svc.generateChatName(chatId, this._currentModel?.server)}
                    />
                    {!hasSystemPrompt && (
                      <ContextMenuItem title="Set System Prompt" onClick={this._onSetSystemPrompt} />
                    )}
                    <ContextMenuItem
                      title="Share Chat"
                      disabled={!chatId}
                      onClick={() => chatId && this._shareChat(chatId)}
                    />
                    {chat?.hasGeneratedFiles && (
                      <ContextMenuItem
                        title={<><ImgFolder size={14} /> View Files</>}
                        onClick={() => chatId && this._onShowFiles(chatId)}
                      />
                    )}
                    <ContextMenuItem
                      title={chat?.isFavorite ? <><ImgStar /> Remove from Favorites</> : <><ImgStar /> Add to Favorites</>}
                      disabled={!chatId}
                      onClick={() => chatId && this._favoriteChat(chatId, !chat?.isFavorite)}
                    />
                    {window.user?.role === 'admin' && (
                      <>
                        <ContextMenuSeparator />
                        <ContextMenuItem
                          title={statsVisibilityService.visible ? 'Hide stats' : 'Show stats'}
                          onClick={() => statsVisibilityService.toggle()}
                        />
                        <ContextMenuItem
                          title="Manage suggestions"
                          onClick={() => this.props.navigate?.('/suggestions')}
                        />
                      </>
                    )}
                    <ContextMenuSeparator />
                    <ContextMenuItem
                      title="Archive Chat"
                      disabled={!chatId}
                      onClick={() => chatId && this._archiveChat(chatId)}
                    />
                    <ContextMenuSeparator />
                    <ContextMenuItem
                      title="Delete Chat"
                      className="danger"
                      disabled={!chatId}
                      onClick={() => chatId && this._deleteChat(chatId)}
                    />
                  </>
                )}
              </ContextMenu>
            </div>
          </div>

          <div className="chat-container">
            <div className="chat-messages-wrapper">
              <BlockLoader isVisible={session.messagesLoader.isPending} size="md" />

              <div className="chat-messages" ref={this._messagesContainerRef} onScroll={this._onMessagesContainerScroll}>
                {!conversation.length && this._currentModel && !session.loading.isPending && (
                  <ChatSuggestions onSelect={this._onChatSuggestionSelect} onImageSuggestionSelect={this._onImageSuggestionSelect} />
                )}

                {conversation.map((msg, index) => {
                  // Approval state lives on the message (survives reload + message-collapse) —
                  // see ChatMessageToolApproval. Show the pending window only while no decision.
                  const approvalFunctionName = msg.pendingToolApproval && !msg.toolApprovalDecision ? msg.pendingToolApproval.toolName : null;
                  return (
                    <ChatMessage
                      key={msg.id || index}
                      chatId={chatId}
                      isGenerating={session.loading.isPending}
                      currentModel={this._currentModel}
                      generatingMessageId={session.generatingMessageId}
                      isLast={index === conversation.length - 1}
                      hasModel={hasModel}
                      message={msg}
                      pendingToolApprovalName={approvalFunctionName}
                      toolApprovalDecision={msg.toolApprovalDecision ?? null}
                      codeHighlightingEnabled={chat?.enableSyntaxHighlighting ?? userService.defaultEnableSyntaxHighlighting}
                      showTokenUsage={userService.showTokenUsage}
                      showFullThinking={chat?.showFullThinking ?? userService.defaultShowFullThinking}
                      chatImageFileList={chatImageFileList.length > 1 ? chatImageFileList : undefined}
                      onRegenerate={this._onMessageRegenerate}
                      onRegenerateFull={this._onMessageRegenerateFull}
                      onRemove={this._onMessageRemove}
                      onUpdated={this._onMessageUpdated}
                      onEditing={this._onMessageEditing}
                      onMessageSelected={this._onMessageSelected}
                      onImageCancel={this._onImageCancel}
                      onShare={this._onShareMessage}
                      onFork={this._onForkMessage}
                      onTakeImageSettings={this._onTakeImageSettings}
                      onToolApprovalDecision={this._onToolApprovalDecision}
                      onUseImageAttachment={this._isImageGenMode ? this._onUseImageAttachment : undefined}
                    />
                  );
                })}

                {upcomingMessages.length > 0 && (
                  <div className="upcoming-section">
                    <div className="upcoming-section-label">Upcoming</div>
                    {upcomingMessages.map((msg) => (
                      <div
                        key={msg.id}
                        className="message user upcoming"
                        title="Click to remove or edit"
                        onClick={() => this._onUpcomingMessageAction(msg)}
                      >
                        <div className="message-content">
                          <div className="message-text">{msg.content}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {session.showFollowUps && chatId && this._currentModel && !session.loading.isPending && conversation.length > 0 && (
                  <FollowUpSuggestions
                    key={session.followUpKey}
                    chatId={chatId}
                    modelName={this._currentModel.name}
                    serverKey={this._currentModel.server}
                    onSelect={this._onFollowUpSelect}
                  />
                )}
              </div>
            </div>

            <TaskPlanPanel chatId={chatId} />

            <PromptArea
              ref={this._promptAreaRef}
              chatId={chatId}
              isVisible={!this._isMessageEditing}
              isGenerating={session.loading.isPending || !!session.generatingMessageId}
              currentModel={this._currentModel}
              isAlternativeMode={session.isAlternativePromptMode}
              hasSystemMessage={hasSystemPrompt}
              tokensInConversation={tokensInConversation}
              showTokenUsage={userService.showTokenUsage}
              tools={chat?.tools ?? session.chatSettings?.tools ?? userService.defaultTools ?? null}
              conversation={tokenConversation}
              predictLength={chat?.predictLength ?? session.chatSettings?.predictLength ?? null}
              onModeSwitch={this._onAlternativePromptModeSwitch}
              onSendMessage={this._sendMessage}
              onSteerMessage={this._onSteerMessage}
              onImageGenerate={this._generateImage}
              onVideoGenerate={this._generateVideo}
              onCancelRequest={this._onCancelRequest}
              onOpenToolsConfig={this._onOpenToolsConfig}
              onImageGenModeChange={this._ononImageGenModeChange}
              skills={skillService.skills}
              onApplySkill={this._onApplySkill}
            />

            {this._modelUpdateAvailable && (
              <React.Suspense fallback={null}>
                <ModelUpdateNotification
                  modelName={this._modelUpdateAvailable.modelName}
                  currentDigest={this._modelUpdateAvailable.currentDigest}
                  latestDigest={this._modelUpdateAvailable.latestDigest}
                  onDismiss={this._onDismissModelUpdate}
                />
              </React.Suspense>
            )}
          </div>
            </>
          )}

          {this._showImagesGallery && (
            <React.Suspense fallback={null}>
              <ImageGallery
                onClose={this._onCloseGallery}
                onNavigateToChat={this._onGalleryNavigateToChat}
              />
            </React.Suspense>
          )}

          {this._filesBrowserPath !== null && chatId && (
            <React.Suspense fallback={null}>
              <FilesBrowser
                chatId={chatId}
                folderPath={this._filesBrowserPath}
                onNavigate={this._onFilesNavigate}
                onClose={this._onCloseFiles}
              />
            </React.Suspense>
          )}
        </div>
        <Modals />
      </>
    );
  }

  private _onCancelRequest = () => {
    const svc = chatExecutionService;
    if (svc.activeChatId) {
      svc.abortSession(svc.activeChatId);
    }
  };

  private _ononImageGenModeChange = (active: boolean) => {
    this._isImageGenMode = active;
  };

  private _loadChatMessages = async (chatId: string) => {
    await chatExecutionService.loadChatMessages(chatId, {
      modelSelectorRef: this._modelSelectorRef,
      sidebarRef: this._sidebarRef,
      onCheckModelUpdate: (model) => this._checkModelUpdate(model),
    });
  };

  @action
  private _onSelectChat = (chatId: string) => {
    this._showImagesGallery = false;
    this._filesBrowserPath = null;
    this._loadChatMessages(chatId);
  };

  @action
  private _onShowGallery = () => {
    this._showImagesGallery = !this._showImagesGallery;
    if (this._showImagesGallery && window.innerWidth < 768) {
      this._sidebarRef.current?.close();
    }
  };

  @action
  private _onCloseGallery = () => {
    this._showImagesGallery = false;
  };

  @action
  private _onShowFiles = (chatId: string) => {
    this._filesBrowserPath = '';
    this.props.navigate?.(`/c/${chatId}/files`);
    if (window.innerWidth < 768) {
      this._sidebarRef.current?.close();
    }
  };

  @action
  private _onFilesNavigate = (path: string) => {
    this._filesBrowserPath = path;
    const chatId = chatExecutionService.activeChatId;
    if (chatId) {
      this.props.navigate?.(path ? `/c/${chatId}/files/${path}` : `/c/${chatId}/files`);
    }
  };

  @action
  private _onCloseFiles = () => {
    this._filesBrowserPath = null;
    const chatId = chatExecutionService.activeChatId;
    this.props.navigate?.(chatId ? `/c/${chatId}` : '/');
  };

  @action
  private _onGalleryNavigateToChat = (chatId: string) => {
    this._onCloseGallery();
    this._loadChatMessages(chatId);
  };

  @action
  private _onToggleSidebarClick = async () => {
    this._sidebarRef.current?.toggle();
    if (this._isSidebarOpen) await chatExecutionService.loadChats(false);
  };

  @action
  private _onAlternativePromptModeSwitch = async (isAlternativeMode: boolean) => {
    const svc = chatExecutionService;
    svc.activeSession.isAlternativePromptMode = isAlternativeMode;
    if (svc.activeChatId) {
      await apiService.put(`/api/chats/${svc.activeChatId}`, { startFromBeginning: isAlternativeMode }, {});
      const chat = svc.chats.find(c => c.id === svc.activeChatId);
      if (chat) chat.startFromBeginning = isAlternativeMode;
    }
  };

  @action
  private _onSearchChatsClick = async () => {
    await this._sidebarRef.current?.search();
  };

  @action
  _generateVideo = async (attachmentIds: string[], prompt: string, model: VideoGenModel, layout: string, size: number, fps: number, length: number) => {
    const svc = chatExecutionService;
    const chatId = svc.activeChatId;
    const isNewChat = !chatId;
    const session = svc.getSession(chatId);
    const conv = svc.getConversation(chatId);
    const conversation = svc.getMessageChain(chatId);
    const parentMessage = conversation[conversation.length - 1];

    let msg = `Generate video with ${model} model, layout: ${layout}`;
    if (prompt) msg += `\n Prompt: ${prompt}`;

    let userMessage: Message = { id: 'u', parentId: parentMessage?.id, role: 'user', content: msg, timestamp: svc.getTimestamp(chatId), source: 'video-tool' };
    if (attachmentIds && attachmentIds.length > 0) {
      userMessage.attachmentIds = attachmentIds;
    }

    const userResponse = await apiService.post<UpdateMessageResult>('/api/chat-messages', { model: 'ComfyUI', chatId, messages: [userMessage] }, { loader: session.loading });
    conv.push(userMessage);

    for (const upd of userResponse.messages) {
      runInAction(() => {
        const m = svc.updateMessageIdInConversation(chatId, upd.oldId, upd.id);
        if (m) { m.attachmentIds = upd.attachmentIds ?? m.attachmentIds; userMessage = m; }
      });
    }

    const assistantMessage: Message = {
      id: 'a', role: 'assistant', content: '', timestamp: svc.getTimestamp(chatId) + 1,
      parentId: userMessage.id, modelName: model, source: 'video-tool',
      toolCalls: [{ id: 'tc-0', name: 'generate_video', arguments: JSON.stringify({ model, imageId: userMessage.attachmentIds?.[0] ?? null, prompt, orientation: layout, size, fps, length }) }],
    };

    const assistantResponse = await apiService.post<UpdateMessageResult>('/api/chat-messages', { model: 'ComfyUI', chatId: userResponse.chatId, messages: [assistantMessage] }, { loader: session.loading });
    conv.push(assistantMessage);
    for (const upd of assistantResponse.messages) {
      runInAction(() => { svc.updateMessageIdInConversation(chatId, upd.oldId, upd.id); });
    }

    if (isNewChat) {
      svc.migrateNewSession(userResponse.chatId);
      runInAction(() => { svc.activeChatId = userResponse.chatId; });
      svc.addNewChat(userResponse.chatId);
      setTimeout(() => svc.generateChatName(userResponse.chatId, svc.currentModel?.server), 5000);
    }

    const activeChatItem = svc.activeChatId ? svc.chats.find(c => c.id === svc.activeChatId) || null : null;
    this.forceUpdate(() => svc.generateResponse(svc.activeChatId, activeChatItem, { source: 'image' }));
  };

  @action
  _generateImage = async (attachmentIds: string[], promptPositive: string, promptNegative: string, model: ImageGenModel, layout: string, styles: ImageStyleSelection[], width?: number, height?: number, transparent?: boolean) => {
    const svc = chatExecutionService;
    const chatId = svc.activeChatId;
    const isNewChat = !chatId;
    const session = svc.getSession(chatId);
    const conv = svc.getConversation(chatId);
    const conversation = svc.getMessageChain(chatId);
    const parentMessage = conversation[conversation.length - 1];

    let msg = `Generate images with ${model} model, layout: ${layout}`;
    if (promptPositive) msg += ` with prompt:\n ${promptPositive}`;
    if (promptNegative) msg += ` \n \n with negative prompt:\n ${promptNegative}`;
    if (styles && styles.length > 0) msg += ` \n \n Using styles: ${styles.map(s => `${parseLoraStyleName(s.name).displayName}${s.strength !== 1 ? ` (strength ${s.strength})` : ''}`).join(', ')}`;

    let userMessage: Message = { id: 'u', parentId: parentMessage?.id, role: 'user', content: msg, timestamp: svc.getTimestamp(chatId), source: 'image-tool' };
    if (attachmentIds && attachmentIds.length > 0) {
      userMessage.attachmentIds = attachmentIds;
    }

    const userResponse = await apiService.post<UpdateMessageResult>('/api/chat-messages', { model: 'ComfyUI', chatId, messages: [userMessage] }, { loader: session.loading });
    conv.push(userMessage);
    for (const upd of userResponse.messages) {
      runInAction(() => {
        const m = svc.updateMessageIdInConversation(chatId, upd.oldId, upd.id);
        if (m) { m.attachmentIds = upd.attachmentIds ?? m.attachmentIds; userMessage = m; }
      });
    }

    const assistantMessage: Message = {
      id: 'a', role: 'assistant', content: '', timestamp: svc.getTimestamp(chatId) + 1,
      parentId: userMessage.id, modelName: model, source: 'image-tool',
      toolCalls: [{ id: 'tc-0', name: 'generate_image', arguments: JSON.stringify({ type: model, attachmentIds: userMessage.attachmentIds, prompt: promptPositive, orientation: layout, styles, ...(width && height ? { width, height } : {}), ...(transparent ? { transparent: true } : {}) }) }],
    };

    const assistantResponse = await apiService.post<UpdateMessageResult>('/api/chat-messages', { model: 'ComfyUI', chatId: userResponse.chatId, messages: [assistantMessage] }, { loader: session.loading });
    conv.push(assistantMessage);
    for (const upd of assistantResponse.messages) {
      runInAction(() => { svc.updateMessageIdInConversation(chatId, upd.oldId, upd.id); });
    }

    if (isNewChat) {
      svc.migrateNewSession(userResponse.chatId);
      runInAction(() => { svc.activeChatId = userResponse.chatId; });
      svc.addNewChat(userResponse.chatId);
      setTimeout(() => svc.generateChatName(userResponse.chatId, svc.currentModel?.server), 5000);
    }

    const activeChatItem = svc.activeChatId ? svc.chats.find(c => c.id === svc.activeChatId) || null : null;
    this.forceUpdate(() => svc.generateResponse(svc.activeChatId, activeChatItem, { source: 'image' }));
  };

  @action
  private _sendMessage = async (msg: string, attachmentIds: string[], startFromBeginning: boolean) => {
    await chatExecutionService.sendMessage(msg, attachmentIds, startFromBeginning);
  };

  /** Queue a steering ("upcoming") message while the model is generating. */
  @action
  private _onSteerMessage = (msg: string) => {
    const chatId = chatExecutionService.activeChatId;
    if (!chatId) return;
    const tempId = `steer-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    chatExecutionService.steerMessage(chatId, msg, tempId);
  };

  /** Show a modal for a queued steering message: remove it from the queue, or
   *  send its content back to the prompt editor for modification. */
  private _onUpcomingMessageAction = (msg: Message) => {
    const chatId = chatExecutionService.activeChatId;
    if (!chatId) return;
    modalService.showModal<null>({
      title: 'Upcoming message',
      width: '500px',
      dialog: () => <div className="upcoming-modal-content">{msg.content}</div>,
      buttons: (window) => [
        {
          type: 'Edit',
          title: 'Edit',
          onClick: () => {
            const content = chatExecutionService.editUpcomingMessage(chatId, msg.id);
            if (content !== null) this._promptAreaRef.current?.setInputValue(content);
            window.close();
          },
        },
        {
          type: ModalButtonType.Remove,
          title: 'Remove',
          color: 'danger',
          onClick: () => {
            chatExecutionService.removeUpcomingMessage(chatId, msg.id);
            window.close();
          },
        },
      ],
    });
  };

  @action
  private _onOpenToolsConfig = async () => {
    const svc = chatExecutionService;
    const session = svc.activeSession;
    session.chatSettings = session.chatSettings || {
      model: userService.effectiveDefaultModel ?? modelService.models[0]?.name ?? null,
      temperature: userService.defaultTemperature ?? 0.8,
      thinkMode: userService.defaultThinkMode ?? 'medium',
      contextLength: userService.defaultContextLength || null,
      enableSyntaxHighlighting: userService.defaultEnableSyntaxHighlighting ?? true,
      whisperLanguage: userService.defaultWhisperLanguage || 'auto',
      showFullThinking: userService.defaultShowFullThinking ?? false,
      tools: userService.defaultTools || [],
      startFromBeginning: false,
    };

    const chat = svc.chats.find(c => c.id === svc.activeChatId);
    const chatSettings: ChatResponseItemSettings = chat || session.chatSettings;

    await modalService.show<ChatConfigDialogProps, string[] | null>(ChatConfigDialog, {
      chat: chatSettings,
      model: this._currentModel,
      onSettingsChanged: async (updatedSettings: ChatResponseItemSettings) => {
        if (chat) {
          await apiService.put(`/api/chats/${chat.id}`, updatedSettings, { loader: svc.chatsLoader });
          Object.assign(chat, updatedSettings);
          return;
        }
        runInAction(() => { session.chatSettings = updatedSettings; });
      },
    }, { closeByEscape: true, closeOnClickOutside: true });
  };

  /** Applies a skill picked from the prompt area's "/" slash-command menu: sets the chat's tools and (if not already set) its system prompt. */
  @action
  private _onApplySkill = async (skill: SkillDto) => {
    const svc = chatExecutionService;
    const chatId = svc.activeChatId;
    const session = svc.getSession(chatId);
    const chat = chatId ? svc.chats.find(c => c.id === chatId) || null : null;
    const tools = skill.tools.length > 0 ? skill.tools : null;

    session.chatSettings = session.chatSettings || {
      model: userService.effectiveDefaultModel ?? modelService.models[0]?.name ?? null,
      temperature: userService.defaultTemperature ?? 0.8,
      thinkMode: userService.defaultThinkMode ?? 'medium',
      contextLength: userService.defaultContextLength || null,
      enableSyntaxHighlighting: userService.defaultEnableSyntaxHighlighting ?? true,
      whisperLanguage: userService.defaultWhisperLanguage || 'auto',
      showFullThinking: userService.defaultShowFullThinking ?? false,
      tools: userService.defaultTools || [],
      startFromBeginning: false,
    };

    if (chat) {
      await apiService.put(`/api/chats/${chat.id}`, { tools }, { loader: svc.chatsLoader });
      runInAction(() => { Object.assign(chat, { tools }); });
    } else {
      runInAction(() => { session.chatSettings = { ...session.chatSettings!, tools }; });
    }

    const conv = svc.getConversation(chatId);
    const hasSystemMessage = conv.length > 0 && conv[0].role === 'system';
    const ts = svc.getTimestamp(chatId);

    if (skill.systemPrompt) {
      if (hasSystemMessage) {
        // Overwrite existing system prompt when a new skill is explicitly applied
        runInAction(() => {
          const systemMsg = conv[0];
          systemMsg.content = skill.systemPrompt;
        });
        toastService.success('Skill applied', `"${skill.name}" system prompt and ${tools ? `${tools.length} tool${tools.length === 1 ? '' : 's'}` : 'all tools'} applied.`);
      } else {
        runInAction(() => {
          conv.unshift({ id: 's', timestamp: ts, role: 'system', content: skill.systemPrompt });
          conv.forEach(m => { if (m.role !== 'system' && !m.parentId) m.parentId = conv[0].id; });
        });
        toastService.success('Skill applied', `"${skill.name}" system prompt and ${tools ? `${tools.length} tool${tools.length === 1 ? '' : 's'}` : 'all tools'} applied.`);
      }
    } else {
      toastService.success('Skill applied', `"${skill.name}" tools applied.`);
    }
  };

  private _autoScrollTimerId: number | null = null;
  private _autoScrollPendingOpts: { force?: boolean; forceBottom?: boolean } = {};

  private _autoScrollMessages(options: { force?: boolean; forceBottom?: boolean } = {}) {
    // Coalesce to one scroll per ~frame: this is called on every streamed
    // token, and issuing overlapping scroll commands (especially smooth
    // ones) restarts each scroll from a moving position, so the container
    // never reaches the bottom and appears stuck.
    if (options.force) {
      this._autoScrollPendingOpts = { force: true };
    } else if (options.forceBottom) {
      this._autoScrollPendingOpts = { forceBottom: true };
    }
    if (this._autoScrollTimerId === null) {
      this._autoScrollTimerId = window.setTimeout(() => {
        this._autoScrollTimerId = null;
        const opts = this._autoScrollPendingOpts;
        this._autoScrollPendingOpts = {};
        this._performAutoScroll(opts);
      }, 33);
    }
  }

  private _performAutoScroll(options: { force?: boolean; forceBottom?: boolean }) {
    const messagesContainer = this._messagesContainerRef.current;
    if (!messagesContainer) return;

    // Generous "near bottom" threshold: a single update (code block, image,
    // tool row) can grow the list by hundreds of px, and a flat 100px
    // threshold made following stop after the first big chunk.
    const nearBottom = Math.max(100, messagesContainer.clientHeight * 0.4);
    const isAtBottom = messagesContainer.scrollHeight - messagesContainer.scrollTop <= messagesContainer.clientHeight + nearBottom;
    if (!(options.force || options.forceBottom || (isAtBottom && !this._disableAutoScroll && !this._isTounched))) return;

    if (options.force && !options.forceBottom) {
      // Position last element's top near the viewport top (discrete events:
      // new message, chat switch) — instant, so it doesn't fight the stream.
      const lastMessage = messagesContainer.lastElementChild as HTMLElement | null;
      if (lastMessage) {
        const compactLog = lastMessage.querySelector('.compact-log') as HTMLElement | null;
        const compactLogHeight = compactLog ? compactLog.clientHeight : 0;
        let top = messagesContainer.scrollHeight - lastMessage.clientHeight - (20 * 2 + 15) + compactLogHeight;
        if (window.innerWidth > 1750) top -= 50;
        messagesContainer.scrollTo({ top });
      } else {
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
      }
    } else if (options.forceBottom) {
      // Discrete user action (send, restore) — smooth is fine here
      messagesContainer.scrollTo({ top: messagesContainer.scrollHeight, behavior: 'smooth' });
    } else {
      // Per-token streaming follow: instant so it keeps up with the stream
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    }
  }

  private _scheduleChat = async (chatId: string) => {
    const chat = chatExecutionService.chats.find(c => c.id === chatId);
    if (!chat) return;
    const modalResult = await modalService.show<ScheduleDialogProps, ScheduleDialogResult>(
      ScheduleDialog,
      { chatId, currentCron: chat.cron ?? null, isScheduled: chat.isScheduled ?? false, currentScheduleModel: chat.scheduleModel ?? null },
      { closeByEscape: true, closeOnClickOutside: true }
    );
    const result = modalResult?.result;
    if (!result) return;
    await apiService.post(`/api/chats/${chatId}/schedule`, { cron: result.cron, isScheduled: result.isScheduled, scheduleModel: result.scheduleModel });
    runInAction(() => {
      const c = chatExecutionService.chats.find(x => x.id === chatId);
      if (c) { c.isScheduled = result.isScheduled; c.cron = result.cron; c.scheduleModel = result.scheduleModel; }
    });
  };

  private _renameChatFromSidebar = async (chatId: string, currentName: string) => {
    const modalResult = await modalService.show<RenameChatDialogProps, string>(
      RenameChatDialog,
      { currentName },
      { closeByEscape: true, closeOnClickOutside: true }
    );
    const newName = modalResult?.result;
    if (!newName) return;
    await apiService.post(`/api/chats/${chatId}/rename`, { name: newName });
    runInAction(() => {
      const chat = chatExecutionService.chats.find(c => c.id === chatId);
      if (chat) chat.name = newName;
      const archived = chatExecutionService.archivedChats.find(c => c.id === chatId);
      if (archived) archived.name = newName;
    });
  };

  /**
   * Shared setup for regeneration: validates the message exists, clears selection,
   * and resolves the base skipMessageId (handling system-message special case).
   * Returns null if the message is not found in the conversation.
   */
  private _prepareRegenerate(message: Message): string | null {
    const svc = chatExecutionService;
    const conv = svc.getConversation(svc.activeChatId);
    if (conv.findIndex(m => m.id === message.id) === -1) return null;
    svc.activeSession.selectedMessageId = null;

    if (message.role === 'system') {
      const msgs = svc.getMessageChain(svc.activeChatId);
      const reversed = [...msgs].reverse();
      return reversed.find(m => m.role === 'assistant')?.id
        || reversed.find(m => m.role === 'user')?.id
        || message.id;
    }
    return message.id;
  }

  /**
   * When the clicked message is part of a tool-call chain (a tool result, an assistant
   * with exactly one tool call, or a collapsed group containing one), resolve the id of
   * the tool-result message to skip: the server then re-executes the tool WITHOUT calling
   * the LLM again for the call itself, and continues generation from the fresh result.
   * Returns null when this is not a tool re-execution — the caller falls back to a full
   * (LLM) regeneration from the clicked message's parent.
   */
  private _resolveToolReExecutionSkipId(message: Message): string | null {
    const svc = chatExecutionService;
    const conv = svc.getConversation(svc.activeChatId);
    const msgs = message.collapsedMessages?.length ? message.collapsedMessages : [message];

    // Exactly one assistant message with tool calls in this chain (a final assistant
    // reply without tool calls doesn't count, so multi-round chains still resolve).
    const toolAssistants = msgs.filter(m => m.role === 'assistant'
      && ((m.toolCalls?.length ?? 0) > 0 || !!m.toolsFunctionName));
    if (toolAssistants.length !== 1) return null;
    const assistant = toolAssistants[0];

    // Only single tool calls are re-executed; multi-call assistants fall back to full regen.
    const callCount = (assistant.toolCalls?.length ?? 0) || (assistant.toolsFunctionName ? 1 : 0);
    if (callCount !== 1) return null;

    // The tool-result message produced by that assistant.
    return conv.find(m => m.role === 'tool' && m.parentId === assistant.id)?.id ?? null;
  }

  @action
  private _onMessageRegenerate = async (message: Message) => {
    const svc = chatExecutionService;
    if (message.role === 'context') {
      if (svc.activeChatId) await svc.regenerateCompaction(svc.activeChatId, message.id);
      return;
    }
    let messageIdToSkip = this._prepareRegenerate(message);
    if (!messageIdToSkip) return;

    // Tool re-execution (re-run just this tool, no new LLM round) applies only to
    // INDIVIDUAL messages — the per-row buttons in the compact thinking log and
    // standalone tool messages. The collapsed message (the footer / context-menu
    // "Regenerate" button) always restarts the whole flow from the user message, so
    // we skip the tool-re-execution shortcut for it.
    if (!message.collapsedMessages) {
      const toolSkipId = this._resolveToolReExecutionSkipId(message);
      if (toolSkipId) messageIdToSkip = toolSkipId;
    }

    const chat = svc.activeChatId ? svc.chats.find(c => c.id === svc.activeChatId) || null : null;
    await svc.generateResponse(svc.activeChatId, chat, { skipMessageId: messageIdToSkip });
  };

  /** Full regeneration (includes LLM call). Used from the "Regenerate full" option in the "..." menu. */
  @action
  private _onMessageRegenerateFull = async (message: Message) => {
    const svc = chatExecutionService;
    const messageIdToSkip = this._prepareRegenerate(message);
    if (!messageIdToSkip) return;

    const chat = svc.activeChatId ? svc.chats.find(c => c.id === svc.activeChatId) || null : null;
    await svc.generateResponse(svc.activeChatId, chat, { skipMessageId: messageIdToSkip });
  };

  @action
  private _onMessageRemove = async (message: Message) => {
    const svc = chatExecutionService;
    const chatId = svc.activeChatId;
    const session = svc.activeSession;
    const chat = chatId ? svc.chats.find(c => c.id === chatId) || null : null;

    const conv = svc.getConversation(chatId);
    const index = conv.findIndex(m => m.id === message.id);
    if (index === -1) return;

    if (message.content && !await modalService.showConfirmation('Are you sure you want to remove this message?', 'Remove message')) return;

    if (message.role === 'system' && message.content) {
      svc.abortSession(chatId);
      // Only delete from server if the chat already exists there (skip for new, unsaved chats)
      if (chat?.id && message.id) await apiService.delete(`/api/messages/${message.id}`);
      const idx = conv.findIndex(m => m.id === message.id);
      if (idx !== -1) conv.splice(idx, 1);
      const msgs = svc.getMessageChain(chatId);
      const lastAssistantMessage = [...msgs].reverse().find(m => m.role === 'assistant');
      if (lastAssistantMessage) svc.generateResponse(chatId, chat, { updateTitle: true, skipMessageId: lastAssistantMessage.id });
      return;
    }

    if (chatId && message.id) {
      const { messages } = messageService.buildMessageTree(conv);
      const messageInTree = messages.find(m => m.id === message.id);
      if (messageInTree?.parent) {
        const idxInParent = messageInTree.parent.children?.findIndex(m => m.id === message.id) ?? -1;
        if (idxInParent !== -1) {
          const nextSibling = messageInTree.parent.children?.[idxInParent + 1];
          if (nextSibling) session.selectedMessageId = nextSibling.id;
        }
      }
      if (messageInTree) {
        const descendantIds = messageService.getDescendantIds(messageInTree);
        if (descendantIds.find(id => id === session.generatingMessageId)) svc.abortSession(chatId);
        const filtered = conv.filter(m => m.id && !descendantIds.includes(m.id));
        conv.length = 0;
        conv.push(...filtered);
      }
      await apiService.post('/api/messages-bulk-remove', { chatId, ids: [message.id] });
    } else {
      conv.splice(index, 1);
    }
  };

  @action
  private _onMessageUpdated = async (message: Message, updatedContent: string, attachmentIds: string[]) => {
    const svc = chatExecutionService;
    const chatId = svc.activeChatId;
    const conv = svc.getConversation(chatId);
    const chat = chatId ? svc.chats.find(c => c.id === chatId) || null : null;

    const index = conv.findIndex(m => m.id === message.id);
    if (index === -1) return;
    if (!updatedContent.trim()) { this._onMessageRemove(message); return; }

    svc.abortSession(chatId);
    svc.activeSession.selectedMessageId = null;

    if (message.role === 'system') {
      conv[index] = { ...message, children: null, parent: null, content: updatedContent };
      const conversation = svc.getMessageChain(chatId);
      const lastAssistantMessage = [...conversation].reverse().find(m => m.role === 'assistant');
      if (lastAssistantMessage) await svc.generateResponse(chatId, chat, { updateTitle: true, skipMessageId: lastAssistantMessage.id });
      return;
    }

    if (message.role === 'user') {
      const newUserMessage: Message = {
        id: 'u', timestamp: svc.getTimestamp(chatId), role: 'user', content: updatedContent,
        parentId: message.parentId, attachmentIds: attachmentIds || [],
      };
      conv.push(newUserMessage);
      const addedMsg = conv[conv.length - 1];
      svc.activeSession.selectedMessageId = addedMsg.id;
      await svc.generateResponse(chatId, chat);
      return;
    }

    throw new Error('Message role not supported for update: ' + message.role);
  };

  @action
  private _onSetSystemPrompt = () => {
    const svc = chatExecutionService;
    const conv = svc.getConversation(svc.activeChatId);
    if (conv.length === 0 || conv[0].role !== 'system') {
      const ts = svc.getTimestamp(svc.activeChatId);
      conv.unshift({ id: 's', timestamp: ts, role: 'system', content: '' });
      conv.forEach(msg => { if (msg.role !== 'system' && !msg.parentId) msg.parentId = conv[0].id; });
    }
  };

  @action
  private _onShowOptionsModal = async () => {
    await modalService.show(SettingsDialog, {}, { closeByEscape: true, closeOnClickOutside: true });
  };

  @action
  private _deleteChat = async (chatId: string) => {
    if (!await modalService.showConfirmation('Are you sure you want to delete this chat?', 'Delete chat')) return;
    const svc = chatExecutionService;
    if (chatId === svc.activeChatId) svc.abortSession(chatId);
    if (chatId) await apiService.delete(`/api/chats/${chatId}`);
    runInAction(() => {
      svc.chats = svc.chats.filter(c => c.id !== chatId);
      svc.archivedChats = svc.archivedChats.filter(c => c.id !== chatId);
      if (svc.activeChatId === chatId) {
        svc.activeChatId = null;
        svc.getConversation(null).length = 0;
      }
    });
  };

  @action
  private _onNewChatClick = async () => {
    const svc = chatExecutionService;
    svc.startNewChat({
      model: userService.effectiveDefaultModel ?? modelService.models[0]?.name ?? null,
      temperature: userService.defaultTemperature ?? 0.8,
      thinkMode: userService.defaultThinkMode ?? 'medium',
      contextLength: userService.defaultContextLength || null,
      enableSyntaxHighlighting: userService.defaultEnableSyntaxHighlighting ?? true,
      whisperLanguage: userService.defaultWhisperLanguage || 'auto',
      showFullThinking: userService.defaultShowFullThinking ?? false,
      tools: userService.defaultTools || [],
      enabledSkills: userService.defaultEnabledSkills || [],
      startFromBeginning: false,
    });
    // New chats always start on the admin-configured default model (no best-model auto-pick)
    if (!this._modelSelectorRef.current?.trySetModel(userService.effectiveDefaultModel ?? '')) {
      this._modelSelectorRef.current?.trySetModel(modelService.models[0]?.name ?? '');
    }
    this._onCloseGallery();
    if (window.innerWidth < 768) this._sidebarRef.current?.close();
    // On the embedded suggestions page, leave it so the new chat view is shown.
    if (this.props.children) this.props.navigate?.('/');
    setTimeout(() => this._promptAreaRef.current?.focus(), 0);
  };

  @action
  private _onMessageEditing = (_: Message, isEditing: boolean) => {
    this._isMessageEditing = isEditing;
  };

  @action
  private _onMessageSelected = (message: Message) => {
    if (message.isStub) {
      chatExecutionService.loadBranch(chatExecutionService.activeChatId, message.id);
    } else {
      chatExecutionService.activeSession.selectedMessageId = message.id ?? null;
    }
  };

  @action
  private _onImageCancel = async (message: Message, promptId: string) => {
    const svc = chatExecutionService;
    const conv = svc.getConversation(svc.activeChatId);
    const parentMessage = conv.find(m => m.id === message.parentId);
    if (!parentMessage) return;
    // The tool message carries its own toolName (always set at tool_start); the parent
    // assistant's toolsFunctionName can be undefined (e.g. standalone image/video gen),
    // which would crash the server's interrupt handler.
    const toolName = message.toolName ?? parentMessage.toolsFunctionName;
    if (!toolName) return;
    await apiService.post('/api/tools/interrupt', {
      chatId: svc.activeChatId,
      operationId: promptId,
      name: toolName,
    }, {
      loader: svc.activeSession.loading,
      headers: { 'x-ollama-server': this._currentModel?.server ?? '' },
    });
  };

  @action
  private _onChatSuggestionSelect = async (suggestion: string, capability: string, imageFile?: File | null) => {
    const svc = chatExecutionService;
    if (capability === 'tools' && !this._currentModel?.capabilities?.includes('tools')) {
      this._modelSelectorRef.current?.setModelWithCapability('tools', ['qwen3.6:35b', 'qwen3.5:35b', 'qwen3.5:122b']);
    }
    if (capability === 'vision' && !this._currentModel?.capabilities?.includes('vision')) {
      this._modelSelectorRef.current?.setModelWithCapability('vision', ['qwen3.6:35b', 'qwen3.5:35b', 'qwen3.5:122b']);
    }
    const message: Message = { id: 'u', timestamp: svc.getTimestamp(svc.activeChatId), role: 'user', content: suggestion };
    if (imageFile && capability === 'vision') {
      const meta = await imageService.uploadAttachment(imageFile);
      message.attachmentIds = [meta.id];
    }
    svc.getConversation(svc.activeChatId).push(message);
    svc.generateResponse(svc.activeChatId, null);
  };

  /** Maps the suggestion's stored model key to the client composer model names. */
  private static _mapSuggestionImageModel(model: string, kind: ImageSuggestionItem['kind']): ImageGenModel | VideoGenModel {
    const m = (model ?? '').trim().toLowerCase();
    if (kind === 'i2v' || kind === 't2v') {
      return m.startsWith('minimax') ? 'MiniMaxH3' : 'Wan2.2';
    }
    switch (m) {
      case 'flux': return 'Flux1.Dev';
      case 'flux2': return 'Flux2.Dev';
      case 'flux2-klein': return 'Flux2.Klein';
      case 'flux-kontext': return 'Flux.Kontext';
      case 'sdxl': return 'SDXL';
      case 'zimage': return 'zImage';
      case 'krea2': return 'krea2';
      case 'qwen-image-2.1': return 'qwen2.1';
      case 'qwen-image-edit': return 'qwen';
      default: return 'Flux1.Dev';
    }
  }

  /** Starter image suggestion: t2x generates immediately; i2x opens the composer with
   *  the prompt (and source image) pre-filled so the user can adjust before running. */
  private _onImageSuggestionSelect = (item: ImageSuggestionItem) => {
    const model = Chat._mapSuggestionImageModel(item.model, item.kind);
    const layout = item.orientation ?? 'square';
    const sourceId = item.sourceImageUrl ? item.sourceImageUrl.split('/').pop() ?? undefined : undefined;
    if (item.kind === 't2i') {
      const styles: ImageStyleSelection[] = item.lora ? [{ name: item.lora, strength: 1 }] : [];
      void this._generateImage([], item.prompt, '', model as ImageGenModel, layout, styles);
    } else if (item.kind === 't2v') {
      void this._generateVideo([], item.prompt, model as VideoGenModel, layout === 'portrait' ? 'portrait' : 'landscape', 480, 16, 7);
    } else {
      const isVideo = item.kind === 'i2v';
      // i2x suggestions without a source image need the user to attach one
      const needsImage = !sourceId;
      this._promptAreaRef.current?.setImageSettings(
        isVideo
          ? { model, prompt: item.prompt, orientation: item.orientation ?? undefined, imageId: sourceId, needsImage }
          : { type: model, prompt: item.prompt, orientation: item.orientation ?? undefined, attachmentIds: sourceId ? [sourceId] : [], needsImage },
        isVideo,
      );
    }
    this._promptAreaRef.current?.focus();
  };

  @action
  private _onFollowUpSelect = async (suggestion: string) => {
    const svc = chatExecutionService;
    const chatId = svc.activeChatId;
    svc.activeSession.showFollowUps = false;
    const chat = chatId ? svc.chats.find(c => c.id === chatId) || null : null;
    const conversation = svc.getMessageChain(chatId);
    const parentMessage = conversation[conversation.length - 1];
    const message: Message = { id: 'u', parentId: parentMessage?.id, role: 'user', content: suggestion, timestamp: svc.getTimestamp(chatId) };
    svc.getConversation(chatId).push(message);
    svc.generateResponse(chatId, chat);
  };

  @action
  private _onForkMessage = async (message: Message) => {
    const svc = chatExecutionService;
    const chatId = svc.activeChatId;
    if (!chatId) return;
    if (!await modalService.showConfirmation('Forking will create a new chat starting from this message. Do you want to continue?', 'Fork chat')) return;
    let targetMessage = message;
    if (message.collapsedMessages) {
      const lastAssistantMessage = [...message.collapsedMessages].reverse().find(m => m.role === 'assistant');
      if (lastAssistantMessage) targetMessage = lastAssistantMessage;
    }
    try {
      const result = await apiService.post<{ chatId: string }>(`/api/chats/${chatId}/fork`, { messageId: targetMessage.id });
      await svc.loadChats(false);
      await this._loadChatMessages(result.chatId);
    } catch (err) {
      console.error('Fork failed:', err);
    }
  };

  @action
  private _onShareMessage = async (message: Message) => {
    const svc = chatExecutionService;
    if (message.collapsedMessages) {
      const lastAssistantMessage = [...message.collapsedMessages].reverse().find(m => m.role === 'assistant');
      if (lastAssistantMessage) message = lastAssistantMessage;
    }
    const chat = svc.chats.find(c => c.id === svc.activeChatId);
    if (!chat) return;
    const conversationName = chat.name || 'Chat with ' + (this._currentModel?.name || 'unknown model');
    await modalService.show(ShareDialog, { 
      chatId: chat.id, 
      conversationName, 
      messageId: message.id, 
      conversation: [message],
      codeHighlightingEnabled: chat.enableSyntaxHighlighting || false,
      showFullThinking: chat.showFullThinking || false
    }, { closeByEscape: true, closeOnClickOutside: true, width: '600px' });
  };

  @action
  private _shareChat = async (chatId: string) => {
    const svc = chatExecutionService;
    const chat = svc.chats.find(c => c.id === chatId);
    const messages = svc.getMessageChain(chatId, false, true);
    const conversationName = chat?.name || 'Chat with ' + (this._currentModel?.name || 'unknown model');
    await modalService.show(ShareDialog, { 
      chatId, 
      conversationName, 
      conversation: messages,
      codeHighlightingEnabled: chat?.enableSyntaxHighlighting || false,
      showFullThinking: chat?.showFullThinking || false
    }, { closeByEscape: true, closeOnClickOutside: true, width: '600px' });
  };

  @action
  _archiveChat = async (chatId: string) => {
    if (!await modalService.showConfirmation('Are you sure you want to archive this chat?', 'Archive chat')) return;
    const svc = chatExecutionService;
    const toArchive = svc.chats.find(c => c.id === chatId);
    await apiService.put(`/api/chats/${chatId}`, { isArchived: true }, {});
    runInAction(() => {
      svc.chats = svc.chats.filter(c => c.id !== chatId);
      if (toArchive && !svc.archivedChats.find(c => c.id === chatId)) svc.archivedChats.unshift({ ...toArchive, isArchived: true });
      if (svc.activeChatId === chatId) {
        svc.activeChatId = null;
        svc.getConversation(null).length = 0;
      }
    });
  };

  @action
  _unarchiveChat = async (chatId: string) => {
    if (!await modalService.showConfirmation('Are you sure you want to unarchive this chat?', 'Unarchive chat')) return;
    await apiService.put(`/api/chats/${chatId}`, { isArchived: false }, {});
    runInAction(() => { chatExecutionService.archivedChats = chatExecutionService.archivedChats.filter(c => c.id !== chatId); });
    await chatExecutionService.loadChats(false);
  };

  @action
  _favoriteChat = async (chatId: string, isFavorite: boolean) => {
    await apiService.put(`/api/chats/${chatId}`, { isFavorite }, {});
    const svc = chatExecutionService;
    const chat = svc.chats.find(c => c.id === chatId) || svc.archivedChats.find(c => c.id === chatId);
    if (chat) chat.isFavorite = isFavorite;
  };

  @action
  private _onTakeImageSettings = (message: Message) => {
    const toolArgs = message.toolsFunctionArguments ? JSON.parse(message.toolsFunctionArguments) : {};
    const isVideo = (message.toolsFunctionName ?? '').includes('video');
    this._promptAreaRef.current?.setImageSettings(toolArgs, isVideo);
  };

  @action
  private _onUseImageAttachment = (attachmentId: string) => {
    this._promptAreaRef.current?.addImageFromAttachmentId(attachmentId);
  };

  @action
  private _onToolApprovalDecision = (toolName: string, decision: ToolApprovalDecision) => {
    chatExecutionService.onToolApprovalDecision(toolName, decision);
  };

  @action
  private async _checkModelUpdate(modelName: string) {
    const userRole = (window as unknown as { user?: { role?: string } }).user?.role || 'guest';
    if (userRole !== 'admin') return;
    const ignoreKey = `model_update_ignored_${modelName}`;
    const lastCheckKey = `model_update_check_${modelName}`;
    const ignoreUntil = localStorage.getItem(ignoreKey);
    if (ignoreUntil && Date.now() < parseInt(ignoreUntil, 10)) return;
    const lastCheck = localStorage.getItem(lastCheckKey);
    if (lastCheck && Date.now() - parseInt(lastCheck, 10) < 24 * 60 * 60 * 1000) return;
    localStorage.setItem(lastCheckKey, Date.now().toString());
    const updateStatus = await modelService.checkModelUpdate(modelName);
    if (updateStatus.hasUpdate) {
      runInAction(() => {
        this._modelUpdateAvailable = {
          modelName,
          currentDigest: updateStatus.currentDigest,
          latestDigest: updateStatus.latestDigest,
        };
      });
    }
  }

  @action
  private _onDismissModelUpdate = () => {
    this._modelUpdateAvailable = null;
  };
}

function ChatRoute({ children }: { children?: React.ReactNode }) {
  const { chatId } = useParams<{ chatId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const filesMatch = /^\/c\/[^/]+\/files(?:\/(.*))?$/.exec(location.pathname);
  const initialFilesPath = filesMatch ? (filesMatch[1] ?? '') : null;
  return (
    <Chat initialChatId={chatId} initialFilesPath={initialFilesPath} navigate={navigate}>
      {children}
    </Chat>
  );
}

export default ChatRoute;